import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type ShippingFulfillmentPromiseFlatTeamSettingGroupId =
  | "shipping_fulfillment_promise.promise_days"
  | "shipping_fulfillment_promise.fulfillment_cutoff";

const groupOwner: SettingOwner = {
  kind: "teamSettings",
  product: "fulfillment",
};

const groupRisk: SettingRisk = {
  level: "low",
  reason:
    "Order cutoff times and promise days behind the shopper-facing delivery estimate.",
};

const orderManagementTarget = {
  type: "settings",
  path: "order-management?section=fulfillment-promise",
} as const;

export const ShippingFulfillmentPromiseFlatTeamSettingGroups = [
  {
    id: "shipping_fulfillment_promise.promise_days",
    title: "Fulfillment promise",
    description: "Set the number of working days it takes to fulfill an order.",
    productArea: "fulfillment",
    access: directMerchantAccess(SettingsPermissions.SET_SHIPPING_SETTINGS),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: orderManagementTarget,
  },
  {
    id: "shipping_fulfillment_promise.fulfillment_cutoff",
    title: "Fulfillment schedule",
    description:
      "The per-day order cutoff times and fulfillment promise days behind the delivery estimate shown to shoppers.",
    productArea: "fulfillment",
    access: directMerchantAccess(SettingsPermissions.SET_SHIPPING_SETTINGS),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: orderManagementTarget,
    synonyms: ["fulfillment cutoff", "shipping fulfillment promise"],
  },
] as const satisfies readonly SettingGroupDefinition<ShippingFulfillmentPromiseFlatTeamSettingGroupId>[];

type _assertAllShippingFulfillmentPromiseFlatTeamSettingGroupIdsProvided =
  ShippingFulfillmentPromiseFlatTeamSettingGroupId extends (typeof ShippingFulfillmentPromiseFlatTeamSettingGroups)[number]["id"]
    ? true
    : never;
const _allShippingFulfillmentPromiseFlatTeamSettingGroupIdsProvided: _assertAllShippingFulfillmentPromiseFlatTeamSettingGroupIdsProvided = true;
void _allShippingFulfillmentPromiseFlatTeamSettingGroupIdsProvided;

export const ShippingFulfillmentPromiseFlatTeamSettingGroupById: Record<
  ShippingFulfillmentPromiseFlatTeamSettingGroupId,
  SettingGroupDefinition<ShippingFulfillmentPromiseFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      ShippingFulfillmentPromiseFlatTeamSettingGroupId,
      SettingGroupDefinition<ShippingFulfillmentPromiseFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of ShippingFulfillmentPromiseFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return map as Record<
    ShippingFulfillmentPromiseFlatTeamSettingGroupId,
    SettingGroupDefinition<ShippingFulfillmentPromiseFlatTeamSettingGroupId>
  >;
})();
